//
/* GS://PROTOCOLO
 * Arquivo: src/pages/AccountsReceivablePage.jsx
 * Versão: 2.0 – Gestão de Recebíveis com Baixa
 * Objetivo: Listagem e operação de contas a receber.
 */

import React, { useState, useEffect } from 'react';
import { 
    getAccountsReceivable, 
    listUsers, 
    getFinancialCategories,
    undoReceive 
} from '../services/firestoreService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddAccountReceivableForm from '../components/forms/AddAccountReceivableForm';
import ReceiveAccountModal from '../components/modals/ReceiveAccountModal';
import { 
    PlusCircle, 
    CheckCircle, 
    Calendar,
    RotateCcw
} from 'lucide-react';

const AccountsReceivablePage = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    
    const [customersMap, setCustomersMap] = useState(new Map());
    const [categoriesMap, setCategoriesMap] = useState(new Map());

    const loadData = async () => {
        setLoading(true);
        try {
            const [accData, usersData, catData] = await Promise.all([
                getAccountsReceivable(),
                listUsers(),
                getFinancialCategories()
            ]);

            setAccounts(accData);

            // Mapear Clientes
            const cMap = new Map();
            const userList = Array.isArray(usersData) ? usersData : (usersData.users || []);
            userList.forEach(u => cMap.set(u.uid || u.id, u.name || u.displayName || u.email));
            setCustomersMap(cMap);

            // Mapear Categorias
            const catMap = new Map();
            catData.forEach(c => catMap.set(c.id, c.name));
            setCategoriesMap(catMap);

        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            toast.error("Falha ao carregar contas.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleReceiveClick = (account) => {
        setSelectedAccount(account);
    };

    const handleUndoClick = async (account) => {
        if (window.confirm(`Deseja estornar o recebimento de "${account.description}"?`)) {
            try {
                setLoading(true);
                await undoReceive(account.id);
                toast.info("Recebimento estornado.");
                await loadData();
            } catch (error) {
                toast.error(error.message);
                setLoading(false);
            }
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    const formatDate = (date) => date ? new Date(date).toLocaleDateString('pt-BR') : '-';

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            received: 'bg-green-100 text-green-800 border-green-200',
            overdue: 'bg-red-100 text-red-800 border-red-200',
            partially_received: 'bg-blue-100 text-blue-800 border-blue-200'
        };
        const labels = {
            pending: 'Pendente',
            received: 'Recebido',
            overdue: 'Atrasado',
            partially_received: 'Parcial'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Calendar className="text-brand-gold" /> Contas a Receber
                    </h1>
                    <p className="text-sm text-gray-500">Controle de entradas futuras</p>
                </div>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center bg-brand-gold text-brand-black hover:bg-yellow-500 font-bold py-2 px-4 rounded-lg shadow transition"
                >
                    <PlusCircle className="mr-2" size={20} />
                    Nova Conta
                </button>
            </div>

            {/* Modais */}
            {showAddForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="w-full max-w-2xl">
                        <AddAccountReceivableForm
                            onClose={() => setShowAddForm(false)}
                            onAccountAdded={loadData}
                        />
                    </div>
                </div>
            )}

            {selectedAccount && (
                <ReceiveAccountModal 
                    account={selectedAccount}
                    onClose={() => setSelectedAccount(null)}
                    onSuccess={loadData}
                />
            )}

            {/* Tabela */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Carregando...</div>
                ) : accounts.length === 0 ? (
                    <div className="p-8 text-center flex flex-col items-center text-gray-400">
                        <CheckCircle size={48} className="mb-2 opacity-20" />
                        <p>Nenhuma conta a receber encontrada.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-gray-100 text-gray-600 uppercase font-bold text-xs">
                                <tr>
                                    <th className="px-6 py-3">Vencimento</th>
                                    <th className="px-6 py-3">Descrição</th>
                                    <th className="px-6 py-3">Cliente</th>
                                    <th className="px-6 py-3">Categoria</th>
                                    <th className="px-6 py-3 text-right">Valor</th>
                                    <th className="px-6 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {accounts.map((acc) => (
                                    <tr key={acc.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-medium text-gray-700">{formatDate(acc.dueDate)}</td>
                                        <td className="px-6 py-4 font-semibold text-gray-900">{acc.description}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {customersMap.get(acc.customerRef?.id || acc.customerRef) || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs">
                                                {categoriesMap.get(acc.categoryRef?.id) || 'Geral'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-gray-800">{formatCurrency(acc.amount)}</td>
                                        <td className="px-6 py-4 text-center">{getStatusBadge(acc.status)}</td>
                                        <td className="px-6 py-4 flex justify-center gap-2">
                                            {acc.status === 'pending' || acc.status === 'overdue' ? (
                                                <button 
                                                    onClick={() => handleReceiveClick(acc)}
                                                    className="flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded hover:bg-green-100 transition font-medium"
                                                    title="Receber Valor"
                                                >
                                                    <CheckCircle size={14} /> Receber
                                                </button>
                                            ) : acc.status === 'received' ? (
                                                <button 
                                                    onClick={() => handleUndoClick(acc)}
                                                    className="flex items-center gap-1 bg-gray-50 text-gray-600 border border-gray-200 px-3 py-1 rounded hover:bg-gray-100 transition font-medium"
                                                    title="Estornar"
                                                >
                                                    <RotateCcw size={14} />
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountsReceivablePage;